if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // Cleared on every real interaction so an aborted or failed re-resolve
        // can't leave the guard stuck on.
        this.isResolvingSelection = false;

        this.resetProductFormState();

        // Remembered so an incompatible combination can be re-resolved around the
        // choice the customer actually just made. See resolveIncompatibleSelection.
        this.lastSelectedOptionValueId = target.dataset.optionValueId;
        this.lastSelectedTargetId = target.id;
        this.lastSelectedPosition = this.optionPositionOf(target);
        this.trackOptionRecency(this.lastSelectedPosition);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // Move the untouched options onto values that exist before asking for the
        // section, so the picker never comes back rendering a dead combination.
        // Skipped when swapping product: the variant data belongs to this product.
        const optionValues = shouldSwapProduct
          ? selectedOptionValues
          : this.resolveAvailableSelection(selectedOptionValues);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, optionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      /**
       * Indexes of the option groups in the order they were last touched, most
       * recent first. Drives which of the customer's earlier choices gets given up
       * when an incompatible selection has to be resolved.
       */
      optionRecency = [];

      /**
       * The option position an input belongs to. Positions are used rather than
       * element references or indexes because updateOptionValues swaps the picker
       * markout out before the resolve runs, detaching anything held onto.
       */
      optionPositionOf(node) {
        const position = parseInt(node?.closest('.product-form__input')?.dataset.optionPosition);
        return Number.isNaN(position) ? null : position;
      }

      trackOptionRecency(position) {
        if (position === null) return;
        this.optionRecency = [position, ...this.optionRecency.filter((existing) => existing !== position)];
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            // The combination the customer assembled doesn't exist, so move the
            // options they didn't just touch to the nearest values that do. Only
            // falls through to unavailable when nothing buyable keeps their choice.
            if (this.resolveIncompatibleSelection(productUrl)) return;
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          // The section comes back by option_values, which Liquid doesn't count
          // as a picked variant, so it still says "From". The customer has
          // picked one now, so the price shown is that variant's own.
          this.querySelectorAll(`#price-${this.dataset.section} .price__from`).forEach((label) => label.remove());
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // The label comes from the re-rendered button, so it says why it's
          // disabled ("Sold out", or "Choose a size" for the custom-size
          // variant that only the Custom size calculator sells).
          const newSubmit = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.productForm?.toggleSubmitButton(
            newSubmit?.hasAttribute('disabled') ?? true,
            newSubmit?.querySelector('span')?.textContent.trim() || window.variantStrings.soldOut
          );
          // Express checkout isn't re-rendered, so carry the custom-variant flag
          // that hides it (buy-buttons.liquid, section-main-product.css).
          this.querySelector('.product-form__buttons')?.toggleAttribute(
            'data-custom-variant',
            Boolean(newSubmit?.closest('.product-form__buttons')?.hasAttribute('data-custom-variant'))
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      /** The option groups in option-position order. */
      get optionGroups() {
        return Array.from(this.querySelectorAll('variant-selects .product-form__input')).sort(
          (a, b) => (parseInt(a.dataset.optionPosition) || 0) - (parseInt(b.dataset.optionPosition) || 0)
        );
      }

      /** Every combination that exists, as [{ id, available, options: [name…] }]. */
      getVariantData() {
        const json = this.querySelector('variant-selects [data-product-variants]')?.textContent;
        if (!json) return [];
        try {
          return JSON.parse(json);
        } catch (error) {
          return [];
        }
      }

      /** The value name currently shown in each option group, by position. */
      getSelectedOptionNames(groups) {
        return groups.map((group) => {
          const select = group.querySelector('select');
          if (select) return select.value;
          return group.querySelector('input:checked')?.value ?? null;
        });
      }

      /**
       * Given the option value ids the picker is about to submit, returns the ids
       * to actually ask for: unchanged when the combination is buyable, otherwise
       * the nearest one that is. Runs before the fetch because the section endpoint
       * answers an impossible combination with a fallback variant rather than
       * nothing, which would leave the customer looking at their own dead selection.
       */
      resolveAvailableSelection(selectedOptionValues) {
        const groups = this.optionGroups;
        const variants = this.getVariantData();
        if (!variants.length || !groups.length) return selectedOptionValues;

        const positions = groups.map((group) => parseInt(group.dataset.optionPosition));
        const pinnedIndex = positions.indexOf(this.lastSelectedPosition);
        const selected = this.getSelectedOptionNames(groups);
        if (pinnedIndex < 0 || !selected[pinnedIndex]) return selectedOptionValues;

        const matches = (variant) => selected.every((name, index) => variant.options?.[index] === name);
        if (variants.some((variant) => variant.available && matches(variant))) return selectedOptionValues;

        const best = this.findClosestVariant(variants, selected, pinnedIndex, positions, this.valueIndexesOf(groups));
        if (!best) return selectedOptionValues;

        const optionValueIds = this.optionValueIdsFor(groups, best.options);
        return optionValueIds.length === groups.length ? optionValueIds : selectedOptionValues;
      }

      /**
       * The combination the customer has assembled may not exist as a variant —
       * picking a new colour can strand them on a size that colour isn't made in.
       *
       * Rather than leaving a dead end, find the available variant that keeps the
       * value they just picked and changes as little else as possible, moving any
       * option it has to give up to the nearest value it can. Options are
       * weighted by how recently they were touched, so with three or more options
       * the earliest, most incidental choice is the one sacrificed — whatever order
       * they were picked in.
       *
       * Returns true when a re-resolve is under way, so the caller skips the
       * unavailable state.
       */
      resolveIncompatibleSelection(productUrl) {
        // isResolvingSelection also guards the nested call below: if the chosen
        // variant somehow still resolves to nothing, we fall through to
        // setUnavailable rather than looping.
        if (this.isResolvingSelection) return false;

        const groups = this.optionGroups;
        const variants = this.getVariantData();

        // Without the variant data there is nothing to search, so fall back to
        // re-resolving from the value they just picked alone.
        if (!variants.length || !groups.length) {
          return this.resolveFromLastSelectionOnly(productUrl);
        }

        const positions = groups.map((group) => parseInt(group.dataset.optionPosition));
        const pinnedIndex = positions.indexOf(this.lastSelectedPosition);
        const selected = this.getSelectedOptionNames(groups);
        if (pinnedIndex < 0 || !selected[pinnedIndex]) {
          return this.resolveFromLastSelectionOnly(productUrl);
        }

        // No available combination keeps the value they just picked, so leave the
        // selection alone and let it show as unavailable rather than moving them
        // onto something they didn't ask for.
        const best = this.findClosestVariant(variants, selected, pinnedIndex, positions, this.valueIndexesOf(groups));
        if (!best) return false;

        const optionValueIds = this.optionValueIdsFor(groups, best.options);
        if (optionValueIds.length !== groups.length) {
          return this.resolveFromLastSelectionOnly(productUrl);
        }

        this.requestResolvedSelection(productUrl, optionValueIds);
        return true;
      }

      /**
       * Scores every available variant that keeps the pinned value and returns the
       * best one. Most of the score is how much of the rest of the selection
       * survives, weighted towards the options touched most recently; ties go to
       * whichever variant sits closest to the abandoned values in the option's own
       * list, so a stranded size moves to its neighbour rather than across the run.
       *
       * Sold-out combinations are never candidates — if nothing buyable keeps the
       * pinned value the caller leaves the selection as the customer made it.
       */
      findClosestVariant(variants, selected, pinnedIndex, positions, valueIndexes = []) {
        // Most recently touched first, pinned option excluded — it is mandatory.
        const recencyIndexes = this.optionRecency
          .map((position) => positions.indexOf(position))
          .filter((index) => index >= 0);

        const priority = [...recencyIndexes, ...selected.map((_, index) => index)]
          .filter((index) => index !== pinnedIndex)
          .filter((index, at, all) => all.indexOf(index) === at);

        const weights = [];
        priority.forEach((optionIndex, position) => {
          weights[optionIndex] = priority.length - position;
        });

        let best = null;
        let bestScore = -1;
        let bestDistance = Infinity;

        variants.forEach((variant) => {
          if (!variant.available) return;
          if (variant.options?.[pinnedIndex] !== selected[pinnedIndex]) return;

          let score = 0;
          let distance = 0;
          priority.forEach((optionIndex) => {
            if (variant.options[optionIndex] === selected[optionIndex]) {
              score += weights[optionIndex];
              return;
            }

            const from = valueIndexes[optionIndex]?.get(selected[optionIndex]);
            const to = valueIndexes[optionIndex]?.get(variant.options[optionIndex]);
            if (from !== undefined && to !== undefined) distance += Math.abs(to - from);
          });

          if (score > bestScore || (score === bestScore && distance < bestDistance)) {
            bestScore = score;
            bestDistance = distance;
            best = variant;
          }
        });

        return best;
      }

      /** Each option group's value names mapped to their position in the picker. */
      valueIndexesOf(groups) {
        return groups.map(
          (group) =>
            new Map(
              Array.from(group.querySelectorAll('[data-option-value-id]')).map((node, index) => [node.value, index])
            )
        );
      }

      /** Maps a variant's option value names back to the ids the picker submits. */
      optionValueIdsFor(groups, optionNames) {
        return groups
          .map((group, index) => {
            const candidates = Array.from(group.querySelectorAll('[data-option-value-id]'));
            const match = candidates.find((node) => node.value === optionNames[index]);
            return match?.dataset.optionValueId;
          })
          .filter(Boolean);
      }

      /** Previous behaviour, kept for when the variant data can't be used. */
      resolveFromLastSelectionOnly(productUrl) {
        if (!this.lastSelectedOptionValueId) return false;
        this.requestResolvedSelection(productUrl, [this.lastSelectedOptionValueId]);
        return true;
      }

      requestResolvedSelection(productUrl, optionValueIds) {
        this.isResolvingSelection = true;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, optionValueIds),
          targetId: this.lastSelectedTargetId,
          callback: (html) => {
            try {
              this.handleUpdateProductInfo(productUrl)(html);
            } finally {
              this.isResolvingSelection = false;
            }
          },
        });
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
